import { useEffect, useRef, useState } from 'react';
import Landing from './pages/Landing.jsx';
import TripPage from './pages/TripPage.jsx';
import { ToastProvider } from './components/Toast.jsx';
import { api } from './lib/api.js';
import { useRoute } from './lib/router.js';

const FALLBACK_META = { gearSuggestions: [], stopKinds: [], tiles: null };

/**
 * Notice that the server is serving a build this page did not come from.
 *
 * Cache headers only settle what happens on the next load, and during an outing
 * the page is opened once and left open for hours. Checked when the tab comes
 * back to the front, which is when a phone returns from sleep, and on a slow
 * timer for a screen that simply stays on.
 *
 * It offers rather than reloads: someone may be halfway through writing their
 * ride, and throwing that away to be a version newer is a bad trade.
 */
function useNewBuild(buildId) {
  const known = useRef(null);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!buildId) return undefined;
    if (known.current === null) known.current = buildId;
    if (buildId !== known.current) setStale(true);
  }, [buildId]);

  useEffect(() => {
    if (stale) return undefined;
    const check = () => {
      if (document.visibilityState !== 'visible' || !known.current) return;
      api
        .meta()
        .then((m) => {
          if (m?.buildId && m.buildId !== known.current) setStale(true);
        })
        // Offline, or the server is mid-restart: nothing worth saying here.
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', check);
    const timer = setInterval(check, 10 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', check);
      clearInterval(timer);
    };
  }, [stale]);

  return stale;
}

export default function App() {
  const route = useRoute();
  const [meta, setMeta] = useState(FALLBACK_META);
  const stale = useNewBuild(meta.buildId);

  // Reference vocabulary lives server-side; the UI degrades to free text if it fails.
  useEffect(() => {
    api.meta().then(setMeta).catch(() => setMeta(FALLBACK_META));
  }, []);

  useEffect(() => {
    document.title = route.name === 'trip' ? 'Sortie · Covoiturage' : 'Covoiturage · sorties grimpe';
  }, [route.name]);

  return (
    <ToastProvider>
      {route.name === 'trip' ? (
        <TripPage key={route.shareToken} shareToken={route.shareToken} meta={meta} />
      ) : (
        <Landing meta={meta} />
      )}

      {stale && (
        <div className="update-bar" role="status">
          <span className="small">Une nouvelle version de l'application est disponible.</span>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>
            Recharger
          </button>
        </div>
      )}
    </ToastProvider>
  );
}
